import styled from '@emotion/styled'
import { ExtraScript } from '@pubstack/common/src/extraScript'
import { DateTime } from 'luxon'
import { ChangeEventHandler, FunctionComponent, useRef } from 'react'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { useCreateExtraScript, useUpdateExtraScript } from '~/api/admin/extra-scripts.api'
import Button from '~/components/Button'
import { Input } from '~/components/Input'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { Modal } from '~/components/Modal'
import { RadioGroup } from '~/components/RadioGroup'
import { ToggleInput } from '~/components/Toggle'
import { isoToLocal } from '~/modules/adm/inventory/sites/extra-scripts/SiteExtraScript'
import { WithClassName } from '~/types/utils'

const ContentFormWrapper = styled.form`
  input[type='file'] {
    display: none;
  }

  display: flex;
  flex-direction: column;
  gap: 16px;
`

const getExtraScriptNameAndDate = (fileName: string) => {
  const regexResult = /(?<fileNameWithoutExtension>.*?)\.js$/.exec(fileName)
  if (!regexResult) {
    throw new Error('File is not a Javascript file')
  }
  if (!regexResult.groups) {
    throw new Error('Could not extract file name')
  }
  const name = regexResult.groups.fileNameWithoutExtension
  const date = DateTime.now().toISO()

  return { name, date }
}

type PureSiteExtraScriptEditModalProps = WithClassName & {
  extraScript?: ExtraScript
}

type ExtraScriptForm = {
  type: 'file' | 'externalUrl'
  extraScript: ExtraScript
  file?: File | null
  externalScriptUrl?: string
}

export const SiteExtraScriptEditModal: FunctionComponent<PureSiteExtraScriptEditModalProps> = ({ extraScript }) => {
  const modal = useGlobalModal()
  const { siteId } = useParams<{ siteId: string }>()

  const { control, handleSubmit, setValue, formState, resetField, getValues } = useForm<ExtraScriptForm>({
    defaultValues: {
      type: 'file',
      extraScript: extraScript ?? {
        beforeStart: false,
        synchronous: false,
        enabled: true,
        name: '',
        date: '',
      },
      externalScriptUrl: '',
      file: null,
    },
    mode: 'onChange',
  })

  const extraScriptForm = useWatch({ control, name: 'extraScript' })
  const { mutateAsync: createExtraScripts } = useCreateExtraScript(siteId!, {
    successToast: `The extra-script ${extraScriptForm.name} was uploaded.`,
    errorToast: `An error occurred while the extra-script ${extraScriptForm.name} was being uploaded.`,
  })
  const { mutateAsync: updateExtraScripts } = useUpdateExtraScript(siteId!, {
    successToast: `The extra-script ${extraScriptForm.name} was updated.`,
    errorToast: `An error occurred while the extra-script ${extraScriptForm.name} was being updated.`,
  })

  const type = useWatch({ control, name: 'type' })
  const isNew = extraScript === undefined
  const canSave = isNew ? extraScriptForm?.name !== '' : formState.isDirty

  const inputRef = useRef<HTMLInputElement>(null)
  const handleUploadClick = () => {
    inputRef.current?.click()
  }
  const handleFileChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    if (event.target.files) {
      setValue('file', event.target.files[0])
      const { name, date } = getExtraScriptNameAndDate(event.target.files[0].name)
      setValue('extraScript.name', name, { shouldDirty: true })
      setValue('extraScript.date', date)
      event.target.value = ''
    }
  }
  const saveExtraScript: SubmitHandler<ExtraScriptForm> = async (form) => {
    if (isNew) {
      const data = new FormData()
      data.append('extraScript', JSON.stringify(form.extraScript))
      if (form.file) {
        data.append('content', form.file)
      }
      await createExtraScripts(data)
    } else {
      await updateExtraScripts(form.extraScript)
    }
    modal.close()
  }

  return (
    <Modal.Content>
      <Modal.Title>{isNew ? 'Add new extra-script' : 'Edit extra-script'}</Modal.Title>
      <Modal.Body>
        <ContentFormWrapper id={'extraScriptForm'} onSubmit={(e) => e.preventDefault()}>
          {isNew && (
            <>
              <div>
                <RadioGroup
                  control={control}
                  name={'type'}
                  options={[
                    { label: 'File', value: 'file', trackingLabel: 'adm-sites-stacks-extra-script-file-set' },
                    { label: 'External Url', value: 'externalUrl', trackingLabel: 'adm-sites-stacks-extra-script-external-set' },
                  ]}
                  onChange={() => {
                    resetField('extraScript')
                    setValue('file', null)
                  }}
                />
              </div>
              <div>
                {type === 'file' ? (
                  <>
                    <input onChange={handleFileChange} ref={inputRef} type={'file'} accept={'text/javascript'} />
                    <Button variant={'primary'} onClick={handleUploadClick} iconName={'upload'} trackingLabel={'adm-sites-stacks-extra-script-file-upload'}>
                      Choose file
                    </Button>
                  </>
                ) : (
                  <Input
                    label={'External script url'}
                    control={control}
                    name={'extraScript.name'}
                    error={formState.errors.extraScript?.name?.message}
                    isMessageBlock={true}
                    rules={{
                      required: { value: true, message: 'Url required' },
                      pattern: {
                        value: /^https?:\/\/.*/,
                        message: 'Invalid url',
                      },
                    }}
                  />
                )}
              </div>
            </>
          )}
          {!!extraScriptForm.name && formState.errors.extraScript?.name === undefined && (
            <div>
              <div>Name: {extraScriptForm.name}</div>
              {extraScriptForm?.date && <div>Date: {extraScriptForm.date ? isoToLocal(extraScriptForm.date) : ''}</div>}
              <div>
                <ToggleInput
                  id={'enabled'}
                  control={control}
                  name={'extraScript.enabled'}
                  onChange={() => {
                    setValue('extraScript.synchronous', false)
                    setValue('extraScript.beforeStart', false)
                  }}
                  trackingLabel={getValues('extraScript.enabled') ? 'adm-sites-stacks-extra-script-enable-unset' : 'adm-sites-stacks-extra-script-enable-set'}
                />{' '}
                Enabled
              </div>
              <div>
                <ToggleInput
                  id={'synchronous'}
                  control={control}
                  name={'extraScript.synchronous'}
                  disabled={!extraScriptForm.enabled}
                  trackingLabel={getValues('extraScript.synchronous') ? 'adm-sites-stacks-extra-script-synchronous-unset' : 'adm-sites-stacks-extra-script-synchronous-set'}
                />{' '}
                Synchronous
              </div>
              <div>
                <ToggleInput
                  id={'beforeStart'}
                  control={control}
                  name={'extraScript.beforeStart'}
                  disabled={!extraScriptForm.enabled}
                  trackingLabel={getValues('extraScript.beforeStart') ? 'adm-sites-stacks-extra-script-early-start-unset' : 'adm-sites-stacks-extra-script-early-start-set'}
                />{' '}
                Early start
              </div>
            </div>
          )}
        </ContentFormWrapper>
      </Modal.Body>
      <Modal.Actions>
        <Button variant={'tertiary'} onClick={modal.close} trackingLabel={'adm-sites-stacks-extra-script-cancel'}>
          Cancel
        </Button>
        <Button variant={'primary'} onClick={handleSubmit(saveExtraScript)} disabled={!canSave} trackingLabel={'adm-sites-stacks-extra-script-save'}>
          {isNew ? 'Add' : 'Save'}
        </Button>
      </Modal.Actions>
    </Modal.Content>
  )
}
